import {cn} from '@tempmee/core'
import {type ButtonHTMLAttributes, type ReactNode, forwardRef} from 'react'
import {CircularProgress} from '../circular-progress'
import {IconButtonVariantsFactory} from './Variants'

export type IconButtonColor = 'primary' | 'secondary' | 'error' | 'inverse'
export type IconButtonAppearance = 'default' | 'outline' | 'filled'

/** @deprecated Legacy single-prop variants. Use `color` + `appearance` instead. */
export type LegacyIconButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'warning'
  | 'light'
  | 'white'
  | 'ghost'

const LEGACY_MAP: Record<
  LegacyIconButtonVariant,
  {color: IconButtonColor; appearance: IconButtonAppearance}
> = {
  primary: {color: 'primary', appearance: 'filled'},
  secondary: {color: 'secondary', appearance: 'filled'},
  tertiary: {color: 'secondary', appearance: 'filled'},
  warning: {color: 'error', appearance: 'filled'},
  light: {color: 'primary', appearance: 'default'},
  white: {color: 'primary', appearance: 'default'},
  ghost: {color: 'secondary', appearance: 'default'},
}

function isLegacyVariant(v: string | undefined): v is LegacyIconButtonVariant {
  return v != null && v in LEGACY_MAP
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The icon to display. Replaced by a spinner while loading. */
  Icon: ReactNode
  /**
   * Semantic color. `inverse` is the on-dark color (white border/icon) — mirrors
   * `Button`'s `inverse` so the two match on a filled surface. @default 'primary'
   */
  color?: IconButtonColor
  /**
   * Visual style: `default` | `outline` | `filled`.
   * Legacy single-prop values (`primary`, `ghost`, …) are accepted via `variant` (deprecated).
   * @default 'filled'
   */
  appearance?: IconButtonAppearance
  /** @deprecated Legacy single-prop style. Use `color` + `appearance`. */
  variant?: IconButtonAppearance | LegacyIconButtonVariant
  /**
   * Button size. `small` (32px) and `medium` (40px) are circular; `large` (48px)
   * is a rounded square per Figma. @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /** Optional caption rendered below the circle. */
  children?: ReactNode
  /** Disables the button and shows a spinner. */
  loading?: boolean
  /** @deprecated Use `loading`. */
  isLoading?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      Icon,
      color,
      appearance,
      variant,
      size,
      loading,
      isLoading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    // `variant` is the legacy single-prop API; map it to color + appearance.
    const legacy = isLegacyVariant(variant)
    const resolvedColor: IconButtonColor = legacy ? LEGACY_MAP[variant].color : (color ?? 'primary')
    const resolvedAppearance: IconButtonAppearance = legacy
      ? LEGACY_MAP[variant].appearance
      : (appearance ?? (variant as IconButtonAppearance | undefined) ?? 'filled')

    const busy = loading ?? isLoading
    const circleClass = IconButtonVariantsFactory({
      appearance: resolvedAppearance,
      color: resolvedColor,
      size,
    })
    const spinner = <CircularProgress size={16} color="inherit" />

    if (children != null) {
      return (
        <button
          className={cn(
            'group inline-flex w-[50px] cursor-pointer flex-col items-center gap-1 border-none bg-transparent p-0 focus-visible:outline-none disabled:cursor-not-allowed',
            className,
          )}
          ref={ref}
          disabled={busy || disabled}
          aria-busy={busy}
          {...props}
        >
          <span className={circleClass}>{busy ? spinner : Icon}</span>
          <span className="max-w-full truncate text-center font-sans text-sm font-normal leading-[1.12] text-text-secondary group-hover:font-bold group-disabled:text-text-disabled">
            {children}
          </span>
        </button>
      )
    }

    return (
      <button
        className={cn(circleClass, className)}
        ref={ref}
        disabled={busy || disabled}
        aria-busy={busy}
        {...props}
      >
        {busy ? spinner : Icon}
      </button>
    )
  },
)

IconButton.displayName = 'IconButton'
